import { create } from "zustand";
import { apiClient } from "src/services/apiClient";
import type {
  LayoutDocument,
  UserLayoutListItem,
} from "../shared/util/src/layout";
import {
  payloadToLayoutDocument,
  type UserLayoutPayload,
} from "../shared/util/src/layout";

interface UserLayoutState {
  list: UserLayoutListItem[] | null;
  current: (UserLayoutPayload & { id: string }) | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface UserLayoutActions {
  listLayouts: (token: string) => Promise<void>;
  getLayout: (token: string, id: string) => Promise<LayoutDocument | null>;
  createLayout: (
    token: string,
    data: {
      name: string;
      type: string;
      canvasWidth: number;
      canvasHeight: number;
      backgroundColor?: string | null;
      themeId?: string | null;
      targetKey?: string | null;
      disciplineKey?: string | null;
    },
  ) => Promise<UserLayoutListItem | null>;
  updateLayout: (
    token: string,
    id: string,
    payload: Partial<UserLayoutPayload>,
  ) => Promise<boolean>;
  deleteLayout: (token: string, id: string) => Promise<boolean>;
  duplicateLayout: (
    token: string,
    id: string,
    data?: { name?: string },
  ) => Promise<UserLayoutListItem | null>;
  uploadLayoutImage: (
    token: string,
    file: File,
  ) => Promise<{ imageUrl: string; fileName: string } | null>;
  uploadLayoutVideo: (
    token: string,
    file: File,
  ) => Promise<{ videoUrl: string; fileName: string } | null>;
  uploadLayoutFont: (
    token: string,
    file: File,
  ) => Promise<{
    fileName: string;
    fontUrl: string;
    format: "woff2" | "ttf" | "otf";
    family: string;
  } | null>;
  listLayoutFonts: (token: string) => Promise<
    Array<{
      fileName: string;
      fontUrl: string;
      format: "woff2" | "ttf" | "otf";
      family: string;
    }>
  >;
  setError: (error: string | null) => void;
  resetCurrent: () => void;
}

async function api<T>(
  endpoint: string,
  _token: string,
  options: {
    method?: "GET" | "POST" | "PATCH" | "DELETE";
    body?: unknown;
  } = {},
): Promise<T> {
  return apiClient.request<T>(endpoint, {
    method: options.method ?? "GET",
    body: options.body,
  });
}

export const useUserLayoutStore = create<UserLayoutState & UserLayoutActions>(
  (set, get) => ({
    list: null,
    current: null,
    isLoading: false,
    isSaving: false,
    error: null,

    listLayouts: async (token: string) => {
      set({ isLoading: true, error: null });
      try {
        const list = await api<UserLayoutListItem[]>(
          "/user-layouts?includeScene=false",
          token,
        );
        set({ list });
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "LIST_LAYOUTS_ERROR",
        });
      } finally {
        set({ isLoading: false });
      }
    },

    getLayout: async (token: string, id: string) => {
      set({ isLoading: true, error: null });
      try {
        const raw = await api<UserLayoutPayload & { id: string }>(
          `/user-layouts/${id}`,
          token,
        );
        set({ current: raw });
        return payloadToLayoutDocument(raw);
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "GET_LAYOUT_ERROR",
        });
        return null;
      } finally {
        set({ isLoading: false });
      }
    },

    createLayout: async (token, data) => {
      set({ isSaving: true, error: null });
      try {
        const created = await api<UserLayoutListItem>("/user-layouts", token, {
          method: "POST",
          body: data,
        });
        const list = get().list ?? [];
        set({ list: [created, ...list] });
        return created;
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "CREATE_LAYOUT_ERROR",
        });
        return null;
      } finally {
        set({ isSaving: false });
      }
    },

    updateLayout: async (token, id, payload) => {
      set({ isSaving: true, error: null });
      try {
        const updated = await api<UserLayoutListItem>(
          `/user-layouts/${id}`,
          token,
          {
            method: "PATCH",
            body: payload,
          },
        );
        set({
          list: (get().list ?? []).map((layout) =>
            layout.id === id ? { ...layout, ...updated } : layout,
          ),
          isSaving: false,
        });
        return true;
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "UPDATE_LAYOUT_ERROR",
          isSaving: false,
        });
        return false;
      }
    },

    deleteLayout: async (token, id) => {
      set({ error: null });
      try {
        await api(`/user-layouts/${id}`, token, {
          method: "DELETE",
        });
        const list = (get().list ?? []).filter((l) => l.id !== id);
        set({ list, current: get().current?.id === id ? null : get().current });
        return true;
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "DELETE_LAYOUT_ERROR",
        });
        return false;
      }
    },

    duplicateLayout: async (token, id, data) => {
      set({ isSaving: true, error: null });
      try {
        const duplicated = await api<UserLayoutListItem>(
          `/user-layouts/${id}/duplicate`,
          token,
          { method: "POST", body: data ?? {} },
        );
        const list = get().list ?? [];
        set({ list: [duplicated, ...list] });
        return duplicated;
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "DUPLICATE_LAYOUT_ERROR",
        });
        return null;
      } finally {
        set({ isSaving: false });
      }
    },

    uploadLayoutImage: async (_token, file) => {
      set({ error: null });
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await apiClient.authorizedFetch("/user-layouts/images", {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `HTTP ${res.status}`);
        }
        const data = (await res.json()) as {
          imageUrl: string;
          fileName: string;
        };
        return data;
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "UPLOAD_LAYOUT_IMAGE_ERROR",
        });
        return null;
      }
    },

    uploadLayoutVideo: async (_token, file) => {
      set({ error: null });
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await apiClient.authorizedFetch("/user-layouts/videos", {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `HTTP ${res.status}`);
        }
        const data = (await res.json()) as {
          videoUrl: string;
          fileName: string;
        };
        return data;
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "UPLOAD_LAYOUT_VIDEO_ERROR",
        });
        return null;
      }
    },

    uploadLayoutFont: async (_token, file) => {
      set({ error: null });
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await apiClient.authorizedFetch("/user-layouts/fonts", {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `HTTP ${res.status}`);
        }
        const data = (await res.json()) as {
          fileName: string;
          fontUrl: string;
          format: "woff2" | "ttf" | "otf";
          family: string;
        };
        return data;
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "UPLOAD_LAYOUT_FONT_ERROR",
        });
        return null;
      }
    },

    listLayoutFonts: async (token: string) => {
      set({ error: null });
      try {
        const fonts = await api<
          Array<{
            fileName: string;
            fontUrl: string;
            format: "woff2" | "ttf" | "otf";
            family: string;
          }>
        >("/user-layouts/fonts", token);
        return fonts;
      } catch (e) {
        set({
          error: e instanceof Error ? e.message : "LIST_LAYOUT_FONTS_ERROR",
        });
        return [];
      }
    },

    setError: (error) => set({ error }),
    resetCurrent: () => set({ current: null }),
  }),
);
